import type { NoteNameSystem, KeySig } from '@simplenotation/theory';
import { noteLabel, spellMidi } from '@simplenotation/theory';
import type { VizTheme } from './theme';

export type { NoteNameSystem, KeySig };

/** A note shown on a visualizer. */
export interface VizNote {
  /** Sounding MIDI. */
  midi: number;
  /** Tab string (1 = highest). Placed automatically when missing. */
  string?: number;
  /** Fret relative to the capo. */
  fret?: number;
  /** Text in the dot; default: the note name in the chosen system. */
  label?: string;
}

export interface VizOptionsBase {
  noteNames: NoteNameSystem;
  key: KeySig;
  /** Clicking/tapping enters notes (hover highlight + onPick). */
  touchInput: boolean;
  theme?: Partial<VizTheme>;
}

export const DEFAULT_KEY: KeySig = { fifths: 0, mode: 'major' };

/** Display label of a note (explicit label, else spelled in the key). */
export function labelOf(n: VizNote, system: NoteNameSystem, key: KeySig): string {
  if (n.label !== undefined) return n.label;
  if (system === 'none') return '';
  return noteLabel(spellMidi(Math.round(n.midi), key.fifths), system, key);
}

export function sameNotes(a: readonly VizNote[], b: readonly VizNote[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x.midi !== y.midi || x.string !== y.string || x.fret !== y.fret || x.label !== y.label) return false;
  }
  return true;
}

export function copyNotes(a: readonly VizNote[]): VizNote[] {
  return a.map((n) => ({ midi: n.midi, string: n.string, fret: n.fret, label: n.label }));
}
